// Report Header Generator
// Generates HTML for report headers based on configuration

import moment from 'moment';
import { ReportHeaderConfig, getFieldById } from './reportHeaderFields';

// Helper to get nested property value
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

// Helper to calculate age from DOB
const getAgeFromDOB = (dob: string): string => {
  if (!dob) return '-';
  const birthDate = moment(dob);
  const now = moment();
  const years = now.diff(birthDate, 'years');

  if (years === 0) {
    const months = now.diff(birthDate, 'months');
    if (months === 0) {
      const days = now.diff(birthDate, 'days');
      return `${days}D`;
    }
    return `${months}M`;
  }
  return years.toString();
};

interface GenerateHeaderParams {
  patient: any;
  order: any;
  report: any;
  workflow?: any;
  config?: ReportHeaderConfig;
}

/**
 * Generates HTML for report header based on configuration
 * @param params - Object containing patient, order, report, workflow data and optional config
 * @returns HTML string for the header
 */
export const generateReportHeader = ({
  patient,
  order,
  report,
  workflow,
  config,
}: GenerateHeaderParams): string => {
  if (!config || !config.rows || config.rows.length === 0) {
    return ''; // Return empty if no config
  }

  const data = { patient, order, report, workflow };

  // Generate HTML rows
  const rowsHtml = config.rows
    .map((row) => {
      // Filter out empty/null fields
      const nonEmptyFields = row.filter(cell => cell.field !== null && cell.field !== undefined && cell.field.trim() !== '');

      // Skip row if no fields are selected
      if (nonEmptyFields.length === 0) {
        return '';
      }

      // Determine if this is a single-field row
      const isSingleField = nonEmptyFields.length === 1;

      // Each row has 1 or 2 cells (label-value pairs)
      const cellsHtml = nonEmptyFields
        .map((cell, cellIndex) => {
          const { label, field } = cell;
          const fieldDef = getFieldById(field);

          let value = '-';

          if (fieldDef) {
            // Special handling for different field types
            switch (field) {
              case 'pat_sex_age': {
                const sex = patient?.pat_sex || '-';
                const calculatedAge = getAgeFromDOB(patient?.pat_dob);
                const age = (calculatedAge && calculatedAge !== '-') ? calculatedAge : patient?.pat_age;
                value = `${sex} / ${age}`;
                break;
              }
              case 'pat_age': {
                const calculatedAge = getAgeFromDOB(patient?.pat_dob);
                value = (calculatedAge && calculatedAge !== '-') ? calculatedAge : patient?.pat_age;
                break;
              }
              case 'po_req_time': {
                const rawValue = getNestedValue(data, fieldDef.dataPath);
                value = rawValue ? moment(rawValue).format('DD/MM/YYYY hh:mmA') : '-';
                break;
              }
              case 'report_created_at': {
                const rawValue = getNestedValue(data, fieldDef.dataPath);
                value = rawValue ? moment(rawValue).format('DD/MM/YYYY hh:mmA') : '-';
                break;
              }
              default: {
                const rawValue = getNestedValue(data, fieldDef.dataPath);
                value = rawValue || '-';
              }
            }
          }

          // Add border-right only for the first cell when there are 2 fields
          const borderStyle = !isSingleField && cellIndex === 0 ? '; border-right: 1px solid #000' : '';

          return `
            <div style="flex: 1; display: flex; padding: 3px 6px${borderStyle}">
              <div style="width: 35%; text-align: left">${label}</div>
              <div style="width: 5%; text-align: center">:</div>
              <div style="width: 60%; text-align: left"><strong>${value}</strong></div>
            </div>
          `;
        })
        .join('');

      return `
        <div style="display: flex; border-bottom: 1px solid #000">
          ${cellsHtml}
        </div>
      `;
    })
    .filter(html => html !== '') // Remove empty rows
    .join('');

  // Complete header HTML
  return `
    <div style="border: 1px solid #000; width: 90%; font-size: 10px; margin: auto; padding: 0; max-height: 150px; overflow: hidden; display: flex; align-items: stretch;">
      <div style="flex: 1;">
        ${rowsHtml}
      </div>
    </div>
  `;
};

/**
 * API functions to fetch/save header configuration
 */
export const fetchHeaderConfig = async (makePostCall: any): Promise<ReportHeaderConfig | null> => {
  try {
    const response = await makePostCall('/no-auth/report-header-config', {
      action: 'get',
    });

    if (response?.data?.success && response?.data?.config) {
      return response.data.config;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const saveHeaderConfig = async (
  makePostCall: any,
  config: ReportHeaderConfig
): Promise<boolean> => {
  try {
    const response = await makePostCall('/no-auth/report-header-config', {
      action: 'save',
      config,
    });

    return response?.data?.success === true;
  } catch (error) {
    return false;
  }
};
